import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, input, InputSignal, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageAvailableLanguage } from '@libs/cms/models';
import { CmsLanguageService } from '@libs/shared/common';
import { PageViewService } from '@libs/shared/services';
import { LinkDirective } from '@libs/shared/ui';
import { IconComponent } from '@libs/components/icons';

@Component({
  selector: 'ui-language-navigation',
  imports: [
    UpperCasePipe,
    LinkDirective,
    IconComponent,
  ],
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
  templateUrl: './language-navigation.component.html',
  styleUrl: './language-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageNavigationComponent {
  private readonly cmsLanguageService: CmsLanguageService = inject(CmsLanguageService);
  private readonly pageViewService: PageViewService = inject(PageViewService);
  private readonly elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly menuToggle: Signal<ElementRef<HTMLButtonElement> | undefined> = viewChild('menuToggle');

  public readonly dropdown: InputSignal<boolean> = input(false);
  public readonly menuTitle: InputSignal<string | undefined> = input<string>();
  public readonly menuOpen: WritableSignal<boolean> = signal(false);

  public readonly activeLanguage: Signal<string | undefined> = this.cmsLanguageService.language;

  /** Both the offered languages and their paths are defined by the CMS. */
  public readonly languages: Signal<PageAvailableLanguage[]> = toSignal(
    this.pageViewService.getAvailableLanguages(),
    { initialValue: [] },
  );

  public toggleMenu(): void {
    this.menuOpen.update((open: boolean): boolean => !open);
  }

  public closeMenu(): void {
    this.menuOpen.set(false);
  }

  public onEscape(): void {
    if (this.menuOpen()) {
      this.closeMenu();
      this.menuToggle()?.nativeElement.focus();
    }
  }

  public onDocumentClick(event: MouseEvent): void {
    const target: EventTarget | null = event.target;

    if (this.menuOpen() && target instanceof Node && !this.elementRef.nativeElement.contains(target)) {
      this.closeMenu();
    }
  }
}
